/**
 * A `budget_audit` row as a sentence, instead of as JSON.
 *
 * ── The problem ─────────────────────────────────────────────────────────
 *
 * The Activity tab rendered `JSON.stringify(value)` for every row, so the log an
 * operator opens to answer "who changed this tenant's budget, and to what" read:
 *
 *     tenant_caps    {"free":{"month":200000000},"extra":{"month":50000000}}
 *     storage_quota  5368709120 → 10737418240
 *
 * `200000000` is **$200** — this ledger is USD MICRODOLLARS — and reads as two
 * hundred million. `5368709120` is 5 GB. Both are the entire point of the row and
 * neither is legible without knowing the unit.
 *
 * ── Five rules, each of which is a way to get this wrong ────────────────
 *
 * 1. **The discriminator is `action`, never `subject`.** `subject` is the TARGET
 *    — a role name, a team id, a member email, `override`, `payment`,
 *    `plan_authority` — and is frequently null. Keying on it would produce one
 *    bucket per team id. (The plan for this work said `subject`; it was wrong.)
 * 2. **Money here is USD microdollars**, with ONE exception: `manual_payment`
 *    carries minor units of the BILLING currency. Formatting that with the
 *    microdollar formatter renders MYR 1,200.00 as "$0.0012".
 * 3. **`null` is not `0`.** On a money cap `null` is unlimited and `0` is
 *    blocked — opposites that both look falsy. They are spelled as WORDS here so
 *    a sentence can never conflate them.
 * 4. **The verb comes from comparing the values**, not from the action name.
 *    The same `tenant_caps` row can be a raise, a cut, or a change to one of two
 *    ceilings, and "Raised" printed over a cut is worse than the raw JSON.
 * 5. **Unknown actions fall back to the raw diff**, so a new action type added
 *    tomorrow degrades to today's behaviour instead of throwing or lying.
 *
 * ── Why formatters are injected ─────────────────────────────────────────
 *
 * `formatMoney` lives in this package, but `formatBytes` lives in a UI package
 * this one may not import, and the billing currency is a runtime value the
 * caller already holds. Taking them as parameters keeps the module pure and
 * makes the unit choices explicit at the call site — which is where rule 2 is
 * most likely to be got wrong.
 */

import { formatCurrencyMinor } from './currency.js'
import { PLATFORM_DATE_LOCALE } from './date-format.js'
import { CAPABILITIES, CAP_ROLES, capValueLabel, type CapValue } from './capabilities.js'

/** What the console knows how to say. Anything else falls back to raw JSON. */
export type AuditAction =
  | 'plan_change' | 'tenant_caps' | 'storage_quota' | 'manual_payment'
  | 'platform_settings' | 'enforcement_mode' | 'allowed_models'
  | 'role_cap' | 'team_budget' | 'member_adjustment' | 'tenant_create'
  | 'member_chat_storage' | 'role_chat_storage'
  | 'credit_rate' | 'billing_currency' | 'concurrency_cap'
  | 'chat_cost_visibility' | 'capability_grant' | 'platform_admin_grant'
  | 'feature_pricing' | 'plan_feature_pricing' | 'tenant_feature_pricing'
  | 'contract_price' | 'subscription_term' | 'extra_settlement'
  | 'entitlement_override' | 'sync_exclusions'

/** Structural, so both the API row and the client's `AuditEntry` satisfy it. */
export interface AuditEntryLike {
  action: string
  subject?: string | null
  oldValue?: unknown
  newValue?: unknown
}

export interface AuditFormatters {
  /** USD microdollars → "$200.00". */
  money: (micro: number) => string
  /** Bytes → "5 GB". */
  bytes: (b: number) => string
  /** Minor units of the billing currency → "MYR 1,200.00". Only `manual_payment`
   *  uses this; everything else on this table is microdollars (rule 2). */
  currencyMinor?: (minor: number) => string
  /**
   * BILLING-CURRENCY MICRO-units (1e-6 of the major unit — `billing-micro.ts`'s
   * `BILLING_MICRO_PER_MAJOR`, the unit `tenant_extra_settlements.amount_billing_micro`
   * stores) → "MYR 1,200.00". Only `extra_settlement` uses this. It is a THIRD
   * money unit distinct from both `money` (USD micro) and `currencyMinor`
   * (billing-currency MINOR): reusing `currencyMinor` here would render
   * 50,000,000 as "MYR 500,000.00" instead of "MYR 50.00" — off by 10,000x —
   * and reusing `money` would print it as a USD figure on a non-USD deployment.
   */
  currencyMicro?: (micro: number) => string
  /** A plan id → its display name, where the console has one loaded. */
  planName?: (planId: string) => string
}

export interface AuditDescription {
  /** Human heading, replacing the snake_case action. */
  title: string
  /** One sentence. Empty when the row carries no comparable values. */
  sentence: string
  /**
   * The row's `subject` is an internal DISCRIMINATOR, not a target, and the
   * caller should not print it beside the heading.
   *
   * `subject` is usually the thing acted on — a role name, a team id, a member
   * email — and showing it is the whole point. But for several actions it
   * selects which VARIANT of the action this is, and the title or the sentence
   * has already absorbed it. Those rows rendered as "Who decides
   * plansplan_authority" and "Credit ratecancel_pending": the heading, then the
   * database key for the heading, run together.
   */
  subjectIsInternal: boolean
  /** True when nothing better than the raw diff could be produced — the caller
   *  should then show the raw values it already has. */
  raw: boolean
}

/** Sentinel for "this decoder could not read the stored value" — kept apart
 *  from `null`, which on these axes is a real, meaningful state. */
const UNREADABLE = Symbol('unreadable')

const TITLES: Record<string, string> = {
  plan_change: 'Plan',
  tenant_caps: 'Spending ceilings',
  seat_cap: 'Seat ceiling',
  storage_quota: 'Storage quota',
  concurrency_cap: 'Concurrent agent jobs',
  manual_payment: 'Payment record',
  promo_grant: 'Promotion granted',
  promo_revoke: 'Promotion revoked',
  promo_redeem: 'Promotion redeemed',
  platform_settings: 'Platform setting',
  enforcement_mode: 'Enforcement mode',
  allowed_models: 'Model access',
  model_restriction: 'Model restriction',
  capability_grant: 'Permission default',
  role_cap: 'Role ceiling',
  member_chat_storage: 'Member chat storage',
  role_chat_storage: 'Role chat storage default',
  team_budget: 'Team budget',
  member_adjustment: 'Member adjustment',
  tenant_create: 'Tenant created',
  thresholds: 'Alert thresholds',
  credit_rate: 'Credit rate',
  billing_currency: 'Billing currency',
  display_currency: 'Display currency',
  chat_cost_visibility: 'Chat cost display',
  feature_pricing: 'Feature pricing',
  tenant_feature_pricing: 'Feature pricing (this tenant)',
  plan_feature_pricing: 'Feature pricing (plan)',
  platform_admin_grant: 'Platform administration',
  app_budget: 'App funding',
  platform_seat: 'Platform support seat',
  usage_credit_topup: 'Credit purchased',
  usage_credit_adjust: 'Credit adjusted',
  usage_credit_refund: 'Credit refunded',
  usage_credit_settings: 'Credit settings',
  subscription_term: 'Subscription term added',
  extra_settlement: 'Extra spend settlement',
}

/** `platform_settings` rows carry their own sub-name in `subject`. */
const SETTING_NAMES: Record<string, string> = {
  plan_authority: 'Who decides plans',
  default_plan: 'Default preset for new tenants',
  manual_fallback_plan: 'Non-payment fallback plan',
  // The SUBJECT the PUT route writes is `grace_cap` — it was never
  // `grace_cap_usd`, so this key was dead and every grace-ceiling change fell
  // through to `fallbackTitle`. Caught by the Gate 1 symbol sweep rather than by
  // any test: `describeAuditEntry`'s own suite has no row for it, which is the
  // third time a shape in this file has been wrong in a way only a REAL row
  // shows.
  grace_cap: 'Grace ceiling',
  max_free_orgs_per_user: 'Self-service organizations',
  landing_experience: 'Landing surface',
  chat_cost_visibility: 'Chat cost display',
  // The subject IS the column name, so `camel()` finds the value and
  // `settingValue` renders the boolean as on/off — checked against a real row
  // (0179's PUT writes `{keybookEnabled: false}`), per this file's own rule.
  keybook_enabled: 'Keybook availability',
  // #452 (Task A6). Subject IS the column name (camel() finds
  // `defaultVersionRetention` unaided), and the value is a plain version
  // count — `settingValue` already renders an integer as-is, checked below
  // against the real `{ defaultVersionRetention: N }` shape the PUT route
  // writes.
  default_version_retention: 'Version retention (default)',
  // A SECRET (0200): the row carries `{ configured }`, never the URL — so its
  // stored key is `configured` (SETTING_KEYS below), not `camel(subject)`, and
  // its value renders as configured/not configured rather than a boolean.
  // Checked against a real `{configured:true}` row, per this file's rule.
  disk_alarm_webhook: 'Disk alarm webhook',
}

/**
 * `entitlement_override` (0177) rows carry their axis in `subject` — the same
 * pattern as `platform_settings`, and for the same reason: one action, five
 * unrelated ceilings, so the axis IS the heading rather than a target beside
 * it. `clear_all` is the sixth "axis" ("Return to plan"), not a real one.
 */
const ENTITLEMENT_AXIS_TITLES: Record<string, string> = {
  free: 'Included budget',
  extra: 'Overage cap',
  grace: 'Grace ceiling',
  storage: 'Storage quota',
  concurrency: 'Concurrent agent jobs',
  clear_all: 'Ceilings reset to plan',
}

/** Title-case a snake_case token so an unknown action, or a feature id like
 *  `web_search` / `sandbox_compute`, still reads as words. */
function fallbackTitle(action: string): string {
  const s = action.replace(/_/g, ' ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

/**
 * A money cap, where `null` is a REAL value meaning unlimited and `0` means
 * blocked (rule 3). Returns words, never a bare number, so the two can never be
 * mistaken for each other in a sentence.
 */
function capWords(v: unknown, money: (m: number) => string): string {
  if (v === null) return 'unlimited'
  const n = num(v)
  if (n === null) return '—'
  return n === 0 ? 'blocked' : money(n)
}

/** `{ month: 200000000 }` or a bare number — both shapes appear on this table. */
function monthCap(v: unknown): unknown {
  const o = obj(v)
  if (o && 'month' in o) return o.month
  return v
}

/**
 * The verb, derived from the values rather than the action name (rule 4).
 *
 * `null` means unlimited, which ranks ABOVE every number: unlimited → $50 is a
 * cut, $50 → unlimited is a raise. Getting that backwards is the one outcome
 * worse than the JSON this replaces.
 */
function verb(before: unknown, after: unknown): string {
  const rank = (v: unknown) => (v === null ? Number.POSITIVE_INFINITY : num(v))
  const a = rank(before), b = rank(after)
  if (a === null || b === null) return 'Changed'
  if (b > a) return 'Raised'
  if (b < a) return 'Lowered'
  return 'Left unchanged'
}

/** `plan_authority` → `planAuthority`: the subject is snake_case, the JSONB keys
 *  beside it are camelCase. */
function camel(s: string): string {
  return s.replace(/_([a-z])/g, (_m, c: string) => c.toUpperCase())
}

/**
 * `platform_settings` subjects whose STORED key is not `camel(subject)`.
 *
 * The PUT route writes one audit row per setting changed, keyed by `subject`,
 * and for most of them the subject is the column name so `camel()` finds the
 * value. Two are not: the subject names the SETTING (`default_plan`) while the
 * payload names the COLUMN (`defaultPlanId`). Both rendered as raw JSON on the
 * Operations activity feed — `{"defaultPlanId":null} → {"defaultPlanId":"starter"}`
 * — which is exactly the state this formatter exists to remove, and it was
 * invisible to the tests because they assert on the subjects they were written
 * for. Anything new here should be checked against a real row.
 */
const SETTING_KEYS: Record<string, string> = {
  default_plan: 'defaultPlanId',
  manual_fallback_plan: 'manualFallbackPlanId',
  // NOTE: `disk_alarm_webhook` is NOT here. It has its own early-return branch
  // in the platform_settings case (it renders `{ configured, changed }`, never
  // a value), which runs BEFORE this lookup — so an entry here would be dead.
}

/** Subjects whose value is a plan id, so it can be named rather than printed. */
const PLAN_ID_SETTINGS = new Set(['default_plan', 'manual_fallback_plan'])

function settingValue(v: unknown): string {
  if (v === null || v === undefined) return 'not set'
  if (typeof v === 'boolean') return v ? 'on' : 'off'
  return String(v)
}

/**
 * `chat_cost_visibility` (0174), on BOTH of its axes.
 *
 * The stored value is one of three database keys, and printing them raw would
 * put "user" and "operator" on the feed — two words that read as the SUBJECT of
 * the change ("changed to user"?) rather than as which number the chat shows.
 * The words below say what the reader is actually looking at.
 *
 * `null` differs by axis and both spellings are here on purpose: on a TENANT
 * row it means "inherit the deployment setting", which is a real third state
 * that editor still offers. On the PLATFORM row it resolves to 'hidden' — so
 * the feed says so rather than saying "not set", which invited the reader to
 * assume some other unstated behaviour was in force. Historical rows written
 * before 2026-08-18 carry the same `null` and did NOT mean hidden; they are
 * dated, and the change itself is one of the rows on this feed.
 */
const CHAT_COST_WORDS: Record<string, string> = {
  hidden: 'hidden',
  user: 'what the customer is charged',
  operator: 'what the platform pays',
}

function chatCostWords(v: unknown, whenUnset: string): string {
  if (v === null || v === undefined) return whenUnset
  return typeof v === 'string' ? (CHAT_COST_WORDS[v] ?? v) : String(v)
}

export function describeAuditEntry(
  entry: AuditEntryLike,
  fmt: AuditFormatters,
): AuditDescription {
  const { action, subject } = entry
  const oldV = entry.oldValue
  const newV = entry.newValue

  const title =
    action === 'platform_settings' && subject && SETTING_NAMES[subject]
      ? SETTING_NAMES[subject]
      // `seat_cap` covers two subjects; the included-seats grant (0180) wants its
      // own heading rather than "Seat ceiling".
      : action === 'seat_cap' && subject === 'included_seats'
        ? 'Included seats'
        // `entitlement_override`'s axis IS the heading (see
        // `ENTITLEMENT_AXIS_TITLES`'s own comment) — an unrecognised subject
        // (a future sixth axis) falls through to the generic fallback rather
        // than a blank heading.
        : action === 'entitlement_override' && subject && ENTITLEMENT_AXIS_TITLES[subject]
          ? ENTITLEMENT_AXIS_TITLES[subject]
          : TITLES[action] ?? fallbackTitle(action)

  // Which subjects are variant selectors rather than targets. Listed per
  // action, never as a bare set of strings: `currency` is a discriminator under
  // `billing_currency` and could perfectly well be a real target elsewhere.
  const internalSubjects: Record<string, readonly string[] | 'all'> = {
    platform_settings: 'all',   // the title IS the subject, spelled out
    credit_rate: ['credit_rate', 'cancel_pending'],
    billing_currency: ['currency'],
    manual_payment: ['payment', 'correction'],
    plan_change: ['override', 'fallback_restore'],
    // The feature (and, for a plan override, the plan) is named in the sentence,
    // so the raw `web_search` / `starter:web_search` subject is not repeated.
    // `platform_admin_grant` is NOT here — its subject is the target email.
    feature_pricing: 'all',
    tenant_feature_pricing: 'all',
    plan_feature_pricing: 'all',
    // Both subjects are spelled out by the sentence's noun ("Seat ceiling" /
    // "Included seats"), so the raw subject is not printed beside the title.
    seat_cap: ['max_seats_override', 'included_seats'],
    // `subject` is the plan id `appendTerm` writes — the same plan the
    // sentence already names, so repeating it beside the title would be
    // "Subscription term addedstarter" (rule: never invent a subject the
    // title/sentence already spells out).
    subscription_term: 'all',
    // The axis (or `clear_all`) is already spelled out as the title above, so
    // it is not repeated beside it — same reasoning as `seat_cap`.
    entitlement_override: 'all',
  }
  const rule = internalSubjects[action]
  const subjectIsInternal = !subject || rule === 'all' || (Array.isArray(rule) && rule.includes(subject))

  const nothing: AuditDescription = { title, sentence: '', raw: true, subjectIsInternal }
  const said = (sentence: string): AuditDescription =>
    ({ title, sentence, raw: false, subjectIsInternal })

  switch (action) {
    // ── A platform support seat, granted or revoked (0180) ───────────────
    //
    // `subject` is the seat holder's email and IS printed beside the title —
    // "who" is the whole content of this row. The sentence deliberately states
    // what did NOT change as well as what did: an operator reading this later
    // needs to know a seat is a billing marker, not a grant of access, or they
    // will read "support seat revoked" as "their access was removed".
    case 'platform_seat': {
      const wasOn = obj(oldV)?.platformSeat === true
      const nowOn = obj(newV)?.platformSeat === true
      if (nowOn === wasOn) {
        return said('Left the platform support seat unchanged.')
      }
      return said(nowOn
        ? 'Marked this membership as a platform support seat — AI usage here now bills the platform, not this tenant. Their role and access are unchanged.'
        : 'Removed the platform support seat — AI usage here bills this tenant again. Their role and access are unchanged.')
    }

    // ── The two spending ceilings, which move together on one row ────────
    case 'tenant_caps': {
      const oldFree = monthCap(obj(oldV)?.free)
      const newFree = monthCap(obj(newV)?.free)
      const oldExtra = monthCap(obj(oldV)?.extra)
      const newExtra = monthCap(obj(newV)?.extra)
      const parts: string[] = []
      if (newFree !== undefined && verb(oldFree, newFree) !== 'Left unchanged') {
        parts.push(`${verb(oldFree, newFree).toLowerCase()} the included budget to ${capWords(newFree, fmt.money)}`)
      }
      if (newExtra !== undefined && verb(oldExtra, newExtra) !== 'Left unchanged') {
        parts.push(`${verb(oldExtra, newExtra).toLowerCase()} the overage cap to ${capWords(newExtra, fmt.money)}`)
      }
      // Nothing moved. This is a REAL answer and the most important one to say
      // in words: a row whose old and new values are identical is exactly where
      // the raw JSON was least readable and least informative, because the
      // reader has to diff two long objects by eye to discover that they match.
      // Falling back to `nothing` here would show that JSON.
      if (parts.length === 0) {
        return said(oldV === undefined && newV === undefined
          ? 'No ceiling changed.'
          : 'Re-applied the same ceilings — nothing changed.')
      }
      const s = parts.join(', ')
      return said(`${s.charAt(0).toUpperCase()}${s.slice(1)}.`)
    }

    case 'storage_quota': {
      const before = num(oldV), after = num(newV)
      if (after === null) return nothing
      return said(before === null
        ? `Set to ${fmt.bytes(after)}.`
        : `${verb(before, after)} from ${fmt.bytes(before)} to ${fmt.bytes(after)}.`)
    }

    /**
     * REVIEW ROUND 1, FINDING 12 — both chat-storage actions write rows and
     * neither had a decoder, so the Operations audit screen fell to the raw
     * diff: a bare action string plus `{"limitBytes": null}`.
     *
     * NULL MEANS TWO DIFFERENT THINGS HERE and the raw rendering cannot tell
     * them apart — which is the distinction the whole feature rests on:
     *
     *  - `member_chat_storage` — the member's own column. `null` = INHERIT
     *    the role default. It is NOT "unlimited"; it is "stop having an
     *    opinion about this person".
     *  - `role_chat_storage` — the tenant's role table. A stored
     *    `{limitBytes: null}` IS unlimited, while an ABSENT entry (the outer
     *    value being null) is "reset to the shipped default". Same shape, two
     *    different sentences, which is exactly why the raw JSON misleads.
     *
     * Same trap `role_cap` / `team_budget` document above: one word for null
     * across both axes states the opposite of the truth on one of them.
     */
    case 'member_chat_storage': {
      const unwrap = (v: unknown): number | null | undefined | typeof UNREADABLE => {
        if (v === undefined) return undefined
        if (v === null) return null
        const o = obj(v)
        if (o && 'limitBytes' in o) return o.limitBytes === null ? null : (num(o.limitBytes) ?? UNREADABLE)
        return num(v) ?? UNREADABLE
      }
      const after = unwrap(newV)
      if (after === undefined) return nothing
      // `'—'` for a value this cannot read, exactly as `capWords` does.
      // Falling through to the null word would make "I could not parse this"
      // indistinguishable from "reset to inherit" — rule 3's conflation, on
      // the axis the whole feature rests on. (Scoped review of round 1,
      // finding 11.)
      const words = (v: number | null | typeof UNREADABLE) =>
        v === UNREADABLE ? '—' : v === null ? 'the role default' : v <= 0 ? 'no uploads' : fmt.bytes(v)
      const before = oldV === undefined ? undefined : unwrap(oldV)
      const to = words(after)
      const change = before === undefined || before === after
        ? to
        : `${words(before)} → ${to}`
      return said(subject ? `For ${subject}: ${change}.` : `${change}.`)
    }

    case 'role_chat_storage': {
      // The OUTER null is the reset — the key was removed from the role
      // table, so the shipped default applies again. Distinct from a stored
      // `{limitBytes: null}`, which is an override TO unlimited.
      const words = (v: unknown): string => {
        if (v === null || v === undefined) return 'the shipped default'
        const o = obj(v)
        if (o && 'limitBytes' in o) {
          if (o.limitBytes === null) return 'unlimited'
          const n = num(o.limitBytes)
          // `'—'`, not the reset wording: an unreadable value is not a reset,
          // and saying so would state the opposite of what happened.
          return n === null ? '—' : n <= 0 ? 'no uploads' : fmt.bytes(n)
        }
        const n = num(v)
        return n === null ? '—' : fmt.bytes(n)
      }
      if (newV === undefined) return nothing
      const to = words(newV)
      const change = oldV === undefined ? to : `${words(oldV)} → ${to}`
      return said(subject ? `For ${subject}: ${change}.` : `${change}.`)
    }

    case 'concurrency_cap': {
      if (newV === null) return said('Back to the platform default.')
      const after = num(newV)
      return after === null ? nothing : said(`Set to ${after}.`)
    }

    // These three are WRAPPED, each differently, and the wrapper is not
    // guessable — it was read off the live table:
    //   role_cap           { cap: { month: 15000000 } }        (month may be null)
    //   team_budget        { cap: { month: 22000000 } }
    //   member_adjustment  { budgetAdjustments: { month: 3000000 } }  (or null)
    // Unwrapping via `monthCap` twice would be wrong — the outer key differs, so
    // it is named per action and only the inner `{month}` is shared.
    /**
     * One capability grant, changed from the platform console.
     *
     * TWO subject shapes, because the route writes two kinds of grant:
     *
     *   `<capability>:<role>`        — a ROLE default (the console grid)
     *   `<capability>:app:<appId>`   — a `.miniapp` folder's own grant (Mode B)
     *
     * Either way `subject` is a real target rather than a variant selector, so
     * it stays visible beside the title.
     *
     * **Read the app shape with the role parser and it does not merely lose
     * detail — it takes the literal `'app'` AS the role.** The row then reads
     * "app — Receive org events: …", naming a role that does not exist while
     * the folder id, the only thing identifying WHICH app, vanishes from the
     * sentence. Discriminating on length plus the literal is unambiguous: a
     * role subject is always exactly two parts, and no `CAP_ROLES` id is 'app'.
     *
     * The words come from the capability catalog rather than the stored ids:
     * an operator reading "Viewer: none → read" learns what happened, where
     * "manage_budget:viewer {"level":"read"}" is the row they would have to
     * decode. Both ids remain in `subject` for a support conversation.
     *
     * `level: null` is a RESET, which is a different fact from being set to
     * whatever the resulting value happens to equal — so it is said in those
     * words. What it resets TO differs by subject and is not interchangeable:
     * a role falls to its built-in default, which can be named; an app has no
     * default of its own and simply stops matching, resolving by role instead.
     */
    case 'capability_grant': {
      const parts = (subject ?? '').split(':')
      const capId = parts[0] ?? ''
      // `parts[1] === 'app'` is the whole discriminator, and the trailing
      // segments are re-joined rather than read positionally: a role subject is
      // always exactly two parts and `RoleId` is a closed union with no 'app' in
      // it, so this cannot mistake one for the other, while a pinned length
      // would silently fall back to role-rendering if an id ever carried a
      // colon.
      const isApp = parts.length >= 3 && parts[1] === 'app'
      const roleId = isApp ? '' : (parts[1] ?? '')
      const appId = isApp ? parts.slice(2).join(':') : ''
      const cap = CAPABILITIES.find((c) => c.id === capId)
      // A mini-app is named by its folder id — nothing friendlier is stored,
      // and the id is what a support conversation uses anyway.
      const whoLabel = isApp
        ? `Mini-app ${appId}`
        : (CAP_ROLES.find((r) => r.id === roleId)?.label ?? roleId)
      const words = (v: unknown): string | null => {
        const o = obj(v)
        if (!o || !('level' in o)) return null
        const lvl = o.level
        if (lvl === null) {
          // `bestGrantLevel` stops matching the app subject and the principal
          // resolves on its ROLE, so there is no app default to name here.
          // Naming one would invent it.
          if (isApp) return 'no grant of its own (resolves by role)'
          const fallback = cap ? (cap.defaults as Record<string, CapValue>)[roleId] : undefined
          const named = fallback === undefined ? null : capValueLabel(fallback)
          return named ? `the built-in default (${named})` : 'the built-in default'
        }
        return capValueLabel(cap?.values === 'bool' ? lvl === 'true' || lvl === true : (lvl as CapValue))
      }
      const to = words(newV)
      if (!to) return nothing
      const from = words(oldV)
      const what = cap?.label ?? capId
      return said(from
        ? `${whoLabel} — ${what}: ${from} → ${to}.`
        : `${whoLabel} — ${what}: set to ${to}.`)
    }

    case 'role_cap':
    case 'team_budget':
    case 'member_adjustment': {
      const key = action === 'member_adjustment' ? 'budgetAdjustments' : 'cap'
      const unwrap = (v: unknown) => {
        if (v === undefined) return undefined
        const o = obj(v)
        // An explicit null wrapper means the whole allocation was cleared.
        if (o && key in o) return o[key] === null ? null : monthCap(o[key])
        // Tolerate an unwrapped value, in case an older writer stored one.
        return monthCap(v)
      }
      /**
       * `null` does NOT mean the same thing on these three axes, and reading it
       * as one word for all of them states the opposite of the truth on two.
       *
       *  - `role_cap` is a CEILING: null = the role imposes no limit = unlimited.
       *  - `team_budget` and `member_adjustment` are CONTRIBUTIONS to a member's
       *    ceiling — CLAUDE.md: "a team cap is a CONTRIBUTION to its members'
       *    ceilings; 0 contributes nothing, it does not block". So null there
       *    means *no allocation*, and rendering it "unlimited" claims a team was
       *    granted infinite budget when it was granted none.
       */
      const isCeiling = action === 'role_cap'
      const words = (v: unknown): string =>
        v === null && !isCeiling ? 'none' : capWords(v, fmt.money)

      const rawTo = unwrap(newV)
      if (rawTo === undefined) return nothing
      const to = words(rawTo)
      if (to === '—') return nothing

      // A null/absent OUTER value means there was no prior record at all — the
      // first time this role, team or member was given an allocation. That is
      // NOT the same as a stored `{ cap: { month: null } }`, which is an
      // explicit "unlimited". Reading the first as the second produced
      // "For guest: unlimited → blocked" on a row that only ever set one value,
      // inventing a permission the tenant never had.
      const hadPrior = oldV !== undefined && oldV !== null
      const rawFrom = hadPrior ? unwrap(oldV) : undefined
      const from = rawFrom === undefined ? null : words(rawFrom)
      const change = from === null || from === to ? to : `${from} → ${to}`
      return said(subject ? `For ${subject}: ${change}.` : `${change}.`)
    }

    // `{ pct: [80, 100], absUsd: [100000000] }` — percentage-of-cap alerts and
    // absolute-spend alerts, either of which may be absent.
    case 'thresholds': {
      const o = obj(newV)
      if (!o) return nothing
      const pct = Array.isArray(o.pct) ? o.pct.filter((n): n is number => num(n) !== null) : []
      const abs = Array.isArray(o.absUsd) ? o.absUsd.filter((n): n is number => num(n) !== null) : []
      const bits: string[] = []
      if (pct.length) bits.push(`at ${pct.join('%, ')}% of the ceiling`)
      if (abs.length) bits.push(`at ${abs.map(fmt.money).join(', ')}`)
      return bits.length === 0
        ? said('No alerts set.')
        : said(`Alerts ${bits.join(' and ')}.`)
    }

    // ── Plan ─────────────────────────────────────────────────────────────
    case 'plan_change': {
      // The fallback-restore variant stores a whole caps snapshot, not a plan id.
      if (subject === 'fallback_restore') {
        return said('Restored the ceilings held before the non-payment fallback was applied.')
      }
      const to = obj(newV)?.planId
      if (to === undefined) return nothing
      const name = (v: unknown) =>
        typeof v === 'string' ? (fmt.planName?.(v) ?? v) : 'Free'
      return said(`Moved from ${name(obj(oldV)?.planId)} to ${name(to)}.`)
    }

    // ── Money recorded out of band. MINOR UNITS, not microdollars (rule 2) ─
    case 'manual_payment': {
      const amount = num(obj(newV)?.amountMinor)
      const through = obj(newV)?.paidThrough
      const bits: string[] = []
      if (amount !== null && fmt.currencyMinor) bits.push(`${fmt.currencyMinor(amount)} received`)
      if (typeof through === 'string' && through) {
        const d = new Date(through)
        if (!Number.isNaN(d.getTime())) {
          bits.push(`paid through ${d.toLocaleDateString(PLATFORM_DATE_LOCALE, { day: 'numeric', month: 'short', year: 'numeric' })}`)
        }
      }
      if (bits.length === 0) {
        return subject === 'correction' ? said('Corrected the payment record.') : nothing
      }
      const body = `${bits.join(', ')}.`
      return said(subject === 'correction'
        ? `Correction — ${body}`
        : `${body.charAt(0).toUpperCase()}${body.slice(1)}`)
    }

    /**
     * ONE tenant's bespoke contract price.
     *
     * Amounts are MINOR UNITS of `priceCurrency` — the second exception to
     * rule 2, alongside `manual_payment`. `fmt.currencyMinor` formats in the
     * DEPLOYMENT's billing currency, which is the same currency in every case
     * this route can produce (it defaults the field and refuses an amount with
     * no currency), so it is used directly rather than re-deriving one.
     *
     * `null` is spelled as a word, per rule 3: clearing a contract price is a
     * different event from setting it to zero. Cleared means "charge them the
     * preset's price"; zero means "charge them nothing", which is a deal
     * somebody struck.
     */
    case 'contract_price': {
      const o = obj(oldV)
      const n = obj(newV)
      const flat = num(n?.flatAmountMinor)
      const seat = num(n?.seatPriceMinor)
      const hadFlat = num(o?.flatAmountMinor)
      const hadSeat = num(o?.seatPriceMinor)

      const cycle = (() => {
        const count = num(n?.cycleCount)
        const unit = n?.cycleUnit
        if (count === null || typeof unit !== 'string') return ''
        return count === 1 ? ` per ${unit}` : ` per ${count} ${unit}s`
      })()

      if (flat === null && seat === null) {
        // Distinguish "cleared" from "never had one" — an audit row exists
        // either way, and only the first is an operator taking something away.
        return said(hadFlat !== null || hadSeat !== null
          ? 'Cleared the contract price. They are charged their plan\u2019s price again.'
          : 'Set no contract price.')
      }

      // Format with the ROW's OWN `priceCurrency`, NOT the injected formatter,
      // which is bound to the deployment's CURRENT billing currency. A contract
      // stored in another currency (the route validates any ISO code, and a
      // deployment can re-denominate) would otherwise render "MYR 1,500.00" for a
      // USD 1,500.00 contract \u2014 the confident wrong number this module warns
      // about, exactly as the `credit_rate` case reads its currency off the row.
      // Fall back to the injected formatter (deployment currency) only when the
      // row carries no currency, and to the raw diff if neither is available.
      const cur = typeof n?.priceCurrency === 'string' && n.priceCurrency.length === 3
        ? n.priceCurrency : null
      const money = cur ? (m: number) => formatCurrencyMinor(m, cur) : fmt.currencyMinor
      if (!money) return nothing
      const parts: string[] = []
      if (flat !== null) parts.push(`${money(flat)} flat${cycle}`)
      if (seat !== null) parts.push(`${money(seat)} per seat${cycle}`)
      // The same warning the editor shows: a seat price behind a flat amount
      // never reaches the invoice, and the log should not imply it did.
      const inert = flat !== null && seat !== null ? ' (the per-seat price is inert behind a flat amount)' : ''
      return said(`Contract price: ${parts.join(', ')}${inert}.`)
    }

    /**
     * `tenant_entitlement_overrides` (0177, `createEntitlementOverridesRepository`)
     * — an operator's decision on ONE axis (`free`/`extra`/`grace`/`storage`/
     * `concurrency`), or every axis cleared at once (`subject: 'clear_all'`,
     * "Return to plan").
     *
     * Shapes read off the real repository (`.set`/`.clear`/`.clearAll`), not
     * guessed, per this file's own rule:
     *   set     oldValue: {state,value} | null   newValue: {state,value}
     *   clear   oldValue: {state,value}           newValue: null
     *   clearAll oldValue: [{axis,state,value}]   newValue: null
     * `value` is `null` whenever `state !== 'finite'` — the STATE is the
     * answer there, exactly like `capWords`'s `null` (rule 3), so it is
     * spelled as a word rather than printed as an empty ceiling.
     *
     * `value`'s UNIT depends on the axis, and only the axis tells you which:
     * `free`/`extra`/`grace` are BILLING-CURRENCY MICRO (the same unit
     * `tenant_extra_settlements` uses — `fmt.currencyMicro`, per its own doc
     * comment; NOT USD microdollars like most of this table, and NOT the
     * billing-currency MINOR `manual_payment` uses). `storage` is bytes.
     * `concurrency` is a plain integer. Getting the axis-to-formatter mapping
     * wrong here is the exact failure class rule 2 exists to catch.
     */
    case 'entitlement_override': {
      if (subject === 'clear_all') {
        const removed = Array.isArray(oldV) ? oldV : []
        if (removed.length === 0) return nothing
        const axes = removed
          .map((r) => obj(r)?.axis)
          .filter((a): a is string => typeof a === 'string')
        return said(axes.length
          ? `Cleared ${axes.length} override${axes.length === 1 ? '' : 's'} (${axes.join(', ')}) — back to the plan.`
          : 'Cleared every override — back to the plan.')
      }

      const axis = subject ?? ''
      // `null` return means "cannot render this value" (a money axis with no
      // `currencyMicro` formatter supplied) — the caller degrades the WHOLE
      // row to raw rather than print a bare, unit-less number, matching
      // `extra_settlement`'s stance on the same formatter (rule 2).
      const words = (v: unknown): string | null => {
        const o = obj(v)
        if (!o) return 'inherit from the plan'
        if (o.state === 'unlimited') return 'unlimited'
        if (o.state === 'disabled') return 'disabled (no overage allowed)'
        const n = num(o.value)
        if (n === null) return '—'
        if (axis === 'storage') return fmt.bytes(n)
        if (axis === 'concurrency') return String(n)
        // free / extra / grace — billing-currency micro.
        return fmt.currencyMicro ? fmt.currencyMicro(n) : null
      }

      // Both `.set` and `.clear` always write a `newValue` (an object or an
      // explicit `null`) — `undefined` would mean a row shape this repository
      // never produces, so fall back rather than guess.
      if (newV === undefined) return nothing
      const to = words(newV)
      const from = words(oldV)
      if (to === null || from === null) return nothing
      return said(from === to ? `${to}.` : `${from} → ${to}.`)
    }

    /**
     * A manual-billing subscription term appended for a tenant (0185,
     * `manual_subscription_terms`). Append-only, so `appendTerm` writes no
     * `oldValue` — this is always an announcement, never a diff, unlike
     * every other case in this file.
     *
     * Shape, read off `appendTerm` (`shared/database/src/repositories/
     * subscription-terms.ts`):
     *   { planId, effectiveFrom: ISO string, cycleCount, cycleUnit, note }
     * `subject` duplicates `planId` — it exists so the row can be found by
     * plan, not to be printed a second time — so it is marked internal
     * above and the sentence names the plan itself.
     */
    case 'subscription_term': {
      const n = obj(newV)
      if (!n) return nothing
      const planId = typeof n.planId === 'string' ? n.planId : null
      if (!planId) return nothing
      const planLabel = fmt.planName?.(planId) ?? planId

      // Rendered in UTC, not local time: `effectiveFrom` is always
      // midnight-UTC (`toMidnightUtc` in the repo), and both term timelines
      // (`fmtTermDateUTC` / `formatDateUTC`) already render it that way — a
      // local-time parse here would day-shift the audit row a day earlier
      // than the timeline for any viewer west of UTC.
      const eff = typeof n.effectiveFrom === 'string' ? new Date(n.effectiveFrom) : null
      const when = eff && !Number.isNaN(eff.getTime())
        ? eff.toLocaleDateString(PLATFORM_DATE_LOCALE, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
        : null

      const count = num(n.cycleCount)
      const unit = typeof n.cycleUnit === 'string' ? n.cycleUnit : null
      const cycle = count !== null && unit
        ? (count === 1 ? `every ${unit}` : `every ${count} ${unit}s`)
        : null

      const bits = [planLabel]
      if (when) bits.push(`starting ${when}`)
      if (cycle) bits.push(`billed ${cycle}`)
      const note = typeof n.note === 'string' && n.note ? ` — ${n.note}` : ''
      return said(`${bits.join(', ')}.${note}`)
    }

    // ── Deployment-wide ──────────────────────────────────────────────────
    case 'platform_settings': {
      if (!subject) return nothing
      // The webhook (0200) records only `{ configured }` — never the URL. The
      // route writes a row ONLY when the URL changed, so configured → configured
      // is always a RE-POINT (a new destination), which must read as such rather
      // than as an indistinguishable-from-no-op "configured → configured".
      if (subject === 'disk_alarm_webhook') {
        const wasOn = obj(oldV)?.configured === true
        const nowOn = obj(newV)?.configured === true
        if (wasOn && nowOn) return said('re-pointed to a different URL.')
        return said(`${wasOn ? 'configured' : 'not configured'} → ${nowOn ? 'configured' : 'not configured'}.`)
      }
      const key = SETTING_KEYS[subject] ?? camel(subject)
      const to = obj(newV)?.[key]
      if (to === undefined) return nothing
      // A plan id is named, not printed. "not set → starter" is a row an
      // operator has to go and look up; "Nothing → Starter" is the answer.
      const show = PLAN_ID_SETTINGS.has(subject)
        ? (v: unknown) => (typeof v === 'string' ? (fmt.planName?.(v) ?? v) : 'not set')
        // Unset on THIS axis resolves to 'hidden' (2026-08-18), so the row
        // says so — and still names it as unchosen, because rows written
        // before that date carry the same `null` and did not mean hidden.
        // `settingValue`'s flat "not set" would hide both halves.
        : subject === 'chat_cost_visibility'
          ? (v: unknown) => chatCostWords(v, 'hidden (not set)')
          : settingValue
      return said(`${show(obj(oldV)?.[key])} → ${show(to)}.`)
    }

    /**
     * The credit rate — i.e. the gross margin on AI usage.
     *
     * This case was written against a GUESSED shape (`creditRateMinorPerUsd`)
     * and had never matched a real row: `writeRateEpoch` stores `minorPerUsd`,
     * with the row's own `currency` beside it. The dev database held no
     * `credit_rate` rows when the formatter was written, so the miss was
     * invisible until one was produced deliberately — at which point the most
     * consequential setting on the deployment was rendering as
     * `{"currency":"myr","minorPerUsd":550} → {...}` on the platform feed.
     *
     * Two shapes, one action:
     *  - `subject: 'credit_rate'` — the change, carrying `effectiveFrom`. A
     *    SCHEDULED change and an immediate one are different facts, so which it
     *    was is always said. (It also used to carry `packAction`; 0177 made a
     *    credit pack a face value, so a rate change no longer touches one and
     *    there is nothing to report. HISTORICAL ROWS STILL HOLD THE KEY and it
     *    is deliberately not rendered — describing a pack re-derivation to an
     *    operator who can no longer cause one explains a mechanism that is
     *    gone.)
     *  - `subject: 'cancel_pending'` — a scheduled epoch withdrawn. `newValue`
     *    is `null` here, so the OLD value is the whole story.
     *
     * The currency comes from the ROW, not from `fmt.currencyMinor` — that
     * formatter is bound to the deployment's CURRENT billing currency, and this
     * row may predate a change to it. A past rate printed in today's currency
     * symbol is a confident wrong number, which is what this file exists to
     * stop.
     */
    case 'credit_rate': {
      const one = (v: unknown, cur: unknown): string | null => {
        const n = num(v)
        if (n === null) return null
        return typeof cur === 'string' && cur.length === 3
          ? `${formatCurrencyMinor(n, cur)} per US$1.00`
          : `${n} per US$1.00`
      }
      const o = obj(oldV)
      const n = obj(newV)

      if (subject === 'cancel_pending') {
        const was = one(o?.minorPerUsd, o?.currency)
        return said(was
          ? `Cancelled the scheduled change to ${was}.`
          : 'Cancelled a scheduled rate change.')
      }

      const to = one(n?.minorPerUsd, n?.currency)
      if (to === null) return nothing
      const from = one(o?.minorPerUsd, o?.currency)
      const bits = [from === null ? to : `${from} → ${to}`]

      // A future `effectiveFrom` is the difference between "the margin moved"
      // and "the margin is going to move", which is not something to leave to
      // the reader's arithmetic on an ISO timestamp.
      const eff = typeof n?.effectiveFrom === 'string' ? new Date(n.effectiveFrom) : null
      if (eff && !Number.isNaN(eff.getTime())) {
        const when = eff.toLocaleDateString(PLATFORM_DATE_LOCALE, { day: 'numeric', month: 'short', year: 'numeric' })
        bits.push(eff.getTime() > Date.now() ? `scheduled for ${when}` : `from ${when}`)
      }

      return said(`${bits.join(', ')}.`)
    }

    /** Its sibling, written by the same function when the CURRENCY moves. */
    case 'billing_currency': {
      const to = obj(newV)?.currency
      if (typeof to !== 'string') return nothing
      const from = obj(oldV)?.currency
      return said(typeof from === 'string'
        ? `${from.toUpperCase()} → ${to.toUpperCase()}.`
        : `Set to ${to.toUpperCase()}.`)
    }

    /**
     * Which cost this ONE tenant's chat window shows (0174).
     *
     * The setter writes bare strings — `oldValue`/`newValue` are the column, not
     * a wrapper object — so this reads them directly rather than through
     * `obj()`. Its platform-wide sibling is a `platform_settings` row with
     * `subject: 'chat_cost_visibility'`, handled above and carrying an object.
     * Two shapes, two cases: writing one case that guessed at a wrapper here is
     * exactly how `credit_rate` came to render as raw JSON.
     */
    case 'chat_cost_visibility': {
      const to = chatCostWords(newV, 'follows the platform setting')
      const from = chatCostWords(oldV, 'follows the platform setting')
      return said(`${from} → ${to}.`)
    }

    case 'seat_cap': {
      // TWO subjects on this action, each with its own value key (checked
      // against a real row, per this file's rule):
      //   'max_seats_override' → { maxSeatsOverride: number | null } (setMaxSeatsOverride)
      //   'included_seats'     → { includedSeats:     number | null } (setIncludedSeatsOverride, 0180)
      // `null` = INHERIT the plan's value — rendering it "unlimited" would be
      // wrong whenever the plan carries a limit, which is exactly when an
      // operator clears an override. `0` is a real value (nobody / no free
      // seats), so it must never fall through a truthiness test to the null branch.
      const isIncluded = subject === 'included_seats'
      const key = isIncluded ? 'includedSeats' : 'maxSeatsOverride'
      const noun = isIncluded ? 'Included seats' : 'Seat ceiling'
      const seatText = (v: unknown): string => {
        const n = num(obj(v)?.[key])
        return n === null ? 'inherit from the plan' : `${n} seat${n === 1 ? '' : 's'}`
      }
      return said(`${noun} set to ${seatText(newV)} (was ${seatText(oldV)}).`)
    }

    case 'enforcement_mode': {
      const o = obj(newV)
      const to = o ? (o.enforcementMode ?? o.mode) : newV
      return typeof to === 'string' ? said(`Set to ${to}.`) : nothing
    }

    case 'allowed_models': {
      const list = Array.isArray(newV)
        ? newV
        : Array.isArray(obj(newV)?.models) ? (obj(newV)!.models as unknown[]) : null
      if (list === null) return nothing
      return said(list.length === 0
        ? 'Every model allowed.'
        : `Narrowed to ${list.length} model${list.length === 1 ? '' : 's'}.`)
    }

    /**
     * Per-folder sync rules (#616). Shape, from a real row written by
     * `setSyncExcludePatterns`:
     * `{ patterns: { pattern: string; versions: number | null }[] | null }` on
     * both sides, where a null COLUMN means "never configured" and a null
     * VERSIONS means "this folder is not synced".
     *
     * ── SKIPPED AND CAPPED ARE DIFFERENT SENTENCES ─────────────────────────
     *
     * A rule at `versions: null` stops a folder syncing; one at a count leaves
     * it syncing and caps its history. Folding them into one "changed 2 rules"
     * would make the row useless for the question it exists to answer — and
     * the two have different blast radius, so a reader must not have to open
     * the raw JSON to tell which happened.
     *
     * Newly-SKIPPED folders are named in full rather than counted: the question
     * is "which folder stopped syncing", and the answer is the pattern itself.
     * Everything else is counted — a cap change and a removal can both only
     * ever leave more data in place, which is not the direction anyone
     * investigates.
     */
    case 'sync_exclusions': {
      const list = (v: unknown): { pattern: string; versions: number }[] | null => {
        const p = obj(v)?.patterns
        if (!Array.isArray(p)) return null
        return p.flatMap((x) => {
          const r = obj(x)
          if (typeof r?.pattern !== 'string') return []
          // A row written while "not synced" existed carries `versions: null`.
          // Read as the seeded count, matching `seedFolderRules` — the decoder
          // and the resolver must not describe the same stored row
          // differently. Anything else non-numeric is malformed and dropped.
          const v2 = r.versions === null ? 1 : r.versions
          if (typeof v2 !== 'number') return []
          return [{ pattern: r.pattern, versions: v2 }]
        })
      }
      const to = list(newV)
      if (to === null) return said('Cleared — back to the default folder rules.')
      const from = list(oldV) ?? []
      const was = new Map(from.map((r) => [r.pattern, r.versions]))
      const now = new Map(to.map((r) => [r.pattern, r.versions]))

      // `was.get` returning `undefined` for an absent pattern is what separates
      // a NEW rule from an unchanged one, and a number never equals undefined,
      // so a new rule is always reported.
      const changed = to.filter((r) => was.get(r.pattern) !== r.versions)
      const removed = from.filter((r) => !now.has(r.pattern))
      if (changed.length === 0 && removed.length === 0) return nothing

      const parts: string[] = []
      // Named individually up to three, then counted: a tenant may save a
      // hundred rules at once, and a sentence listing all of them is not a
      // sentence anyone reads.
      if (changed.length > 0) {
        parts.push(changed.length <= 3
          ? `set ${changed.map((r) => `"${r.pattern}" to ${r.versions} version${r.versions === 1 ? '' : 's'}`).join(', ')}`
          : `set a version limit on ${changed.length} folders`)
      }
      if (removed.length > 0) parts.push(`dropped ${removed.length} rule${removed.length === 1 ? '' : 's'}`)
      const sentence = parts.join('; ')
      return said(`${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`)
    }

    case 'tenant_create':
      return said(subject
        ? `Created, with ${subject} invited as owner.`
        : 'Created from the platform console.')

    // ── Promotions ───────────────────────────────────────────────────────
    // `effects` is an ARRAY of `{ kind, … }`. Credit is microdollars like the
    // rest of the ledger — the exception is `manual_payment` alone (rule 2).
    case 'promo_grant':
    case 'promo_redeem': {
      const effects = obj(newV)?.effects
      if (!Array.isArray(effects) || effects.length === 0) return nothing
      const described = effects.map((e) => {
        const o = obj(e)
        if (!o) return null
        const micro = num(o.amountBillingMicro)
        if (o.kind === 'credit' && micro !== null) return `+${fmt.money(micro)} credit`
        // A raised-ceiling effect, or any kind added later: name it rather than
        // dropping it, so the sentence never under-reports what was granted.
        return typeof o.kind === 'string' ? String(o.kind) : null
      }).filter((s): s is string => s !== null)
      if (described.length === 0) return nothing

      const ends = obj(newV)?.endsAt
      let until = ''
      if (typeof ends === 'string' && ends) {
        const d = new Date(ends)
        if (!Number.isNaN(d.getTime())) {
          until = `, expires ${d.toLocaleDateString(PLATFORM_DATE_LOCALE, { day: 'numeric', month: 'short', year: 'numeric' })}`
        }
      }
      return said(`${described.join(', ')}${until}.`)
    }

    case 'promo_revoke':
      return said('Revoked; any credit already spent is not clawed back.')

    /**
     * Who holds platform administration — the highest-consequence row on the
     * feed, and it rendered `{"role":"admin","confers":true}` because it had a
     * title and no case. `subject` is the target EMAIL and stays visible; the
     * sentence is the ROLE move plus whether it grants platform power. The two
     * are independent — a role can change while the grant does not (admin →
     * viewer both confer) and the grant can flip on a role change (→ noaccess) —
     * so the confer clause is derived from `confers`, not from the role name.
     * Shapes off routes.ts:1638: `{ role: string|null, confers: boolean }`.
     */
    case 'platform_admin_grant': {
      const o = obj(oldV), n = obj(newV)
      const fromRole = typeof o?.role === 'string' ? o.role : null
      const toRole = typeof n?.role === 'string' ? n.role : null
      const fromConf = o?.confers === true
      const toConf = n?.confers === true
      // A row where the role did not move is a no-op re-save — `confers` is
      // derived from the role, so an unchanged role is an unchanged grant. Say so
      // rather than emitting a lone role word with no verb ("admin.").
      if (fromRole === toRole) return said('No change.')
      // `fromRole === toRole` already returned above, so the roles differ here.
      const base =
        fromRole === null ? `Added as ${toRole}`
        : toRole === null ? `Removed (was ${fromRole})`
        : `${fromRole} → ${toRole}`
      const clause =
        toConf && !fromConf ? ' — now has platform administration'
        : !toConf && fromConf ? ' — platform administration removed'
        : ''
      return said(`${base}${clause}.`)
    }

    /**
     * The three-layer feature-pricing family, all previously raw JSON. The rate
     * key is not the same across layers — the global default stores `unitRateUsd`
     * with a `unit`, the plan and tenant overrides store `baseRateUsd` and no
     * unit — and both are USD microdollars. `newValue: null` on the two override
     * layers is a DELETE (revert to the default), a real distinct fact.
     *   feature_pricing        subject: <feature>        { chargeable, unitRateUsd, unit }
     *   plan_feature_pricing   subject: <plan>:<feature> { mode, chargeable, baseRateUsd, params }
     *   tenant_feature_pricing subject: <feature>        { mode, chargeable, baseRateUsd, params }
     */
    case 'feature_pricing':
    case 'plan_feature_pricing':
    case 'tenant_feature_pricing': {
      const rateKey = action === 'feature_pricing' ? 'unitRateUsd' : 'baseRateUsd'
      // A price string for one side. `chargeable: false` beats a rate of 0 — the
      // two are different facts and 0 reads as free rather than off.
      const price = (v: unknown): string | null => {
        const p = obj(v)
        if (!p) return null
        if (p.chargeable === false) return 'not charged'
        const rate = num(p[rateKey])
        const unit = typeof p.unit === 'string' && p.unit ? ` per ${p.unit}` : ''
        return rate !== null ? `${fmt.money(rate)}${unit}` : 'charged'
      }
      // The label: the feature, plus the plan for the plan layer (subject is
      // `<plan>:<feature>` there and only there).
      let label: string
      if (action === 'plan_feature_pricing') {
        const [planId, ...rest] = (subject ?? '').split(':')
        const feat = rest.join(':')
        const planName = planId ? (fmt.planName?.(planId) ?? planId) : ''
        label = `${fallbackTitle(feat)}${planName ? ` (${planName})` : ''}`
      } else {
        label = fallbackTitle(subject ?? '')
      }
      const to = price(newV)
      if (to === null) {
        // A removed override reverts to the layer beneath it.
        return newV === null && action !== 'feature_pricing'
          ? said(`${label}: reverted to the default rate.`)
          : nothing
      }
      const from = price(oldV)
      return said(from && from !== to ? `${label}: ${from} → ${to}.` : `${label}: ${to}.`)
    }

    // Per-tenant overrides (P3, 0189) — shapes read off the setters in
    // `tenant-settings.ts` (`setTenantUploads`/`setTenantRateLimits`/
    // `setBillingCycle`) and pinned in audit-copy.test.ts. `billing_cycle` in
    // particular changes the charged amount, so it must not render as raw JSON.
    case 'tenant_uploads': {
      // `canCreateContent`: boolean | null. null → follow the plan; false →
      // uploads paused (the write freeze); true → force-allow regardless.
      if (newV === null || newV === undefined) return said('Set new uploads to follow the plan.')
      return said(newV === false
        ? 'Paused new uploads for this tenant.'
        : 'Allowed new uploads for this tenant.')
    }

    case 'tenant_rate_limits': {
      // `{db?,sdk?,scripts?}` | null; null → follow the plan. Per-principal
      // requests/min for each tier that is set.
      const o = obj(newV)
      if (!o) return said('Set request limits to follow the plan.')
      const parts = (['db', 'sdk', 'scripts'] as const)
        .filter((k) => num(o[k]) !== null)
        .map((k) => `${k} ${num(o[k])}/min`)
      return parts.length
        ? said(`Set request limits: ${parts.join(', ')}.`)
        : said('Set request limits to follow the plan.')
    }

    case 'billing_cycle': {
      // `{cycleCount, cycleUnit}` — the tenant's manual billing rhythm.
      const o = obj(newV)
      const count = o ? num(o.cycleCount) : null
      const unit = o && typeof o.cycleUnit === 'string' ? o.cycleUnit : null
      if (count === null || !unit) return nothing
      return said(`Set the billing rhythm to every ${count} ${unit}${count === 1 ? '' : 's'}.`)
    }

    /**
     * An operator toggled one manual-subscription term's overage as paid or
     * unpaid (`tenant_extra_settlements`, Task 2.3). Shape (read off the
     * repo's `upsert`, `shared/database/src/repositories/extra-settlements.ts`):
     *   { paidAt: ISO string | null, amountBillingMicro, termId }
     * `subject` duplicates `termId` — it exists so the row can be found by
     * term, same reason `subscription_term`'s subject duplicates `planId` —
     * but unlike that case there is no friendlier name to substitute for a
     * term id, so it is left visible beside the title rather than marked
     * internal (this file's rule: only suppress `subject` when the sentence
     * has already absorbed what it would say).
     *
     * `amountBillingMicro` is BILLING MICRO, not the already-minor unit
     * `manual_payment` stores — `fmt.currencyMicro` is the formatter for that
     * (see its own doc comment for why reusing `currencyMinor` here would be
     * off by 10,000x on a 2-decimal currency).
     */
    case 'extra_settlement': {
      const n = obj(newV)
      if (!n) return nothing
      const micro = num(n.amountBillingMicro)
      if (micro === null || !fmt.currencyMicro) return nothing
      const amount = fmt.currencyMicro(micro)

      const paidAt = n.paidAt
      if (typeof paidAt === 'string' && paidAt) {
        const dt = new Date(paidAt)
        const when = !Number.isNaN(dt.getTime())
          ? dt.toLocaleDateString(PLATFORM_DATE_LOCALE, { day: 'numeric', month: 'short', year: 'numeric' })
          : null
        return said(when
          ? `Marked ${amount} of overage as paid, on ${when}.`
          : `Marked ${amount} of overage as paid.`)
      }
      // `paidAt: null` is a real state (unpaid), not an absent value.
      return said(`Marked ${amount} of overage as unpaid.`)
    }

    // Everything else — including every action added after this file — renders
    // the raw diff, exactly as the screen did before this existed (rule 5).
    default:
      return nothing
  }
}
